/**
 * Различные утилиты
 * @module
 */

import axios from "axios"

import { extractOracleErrorMessage } from "~/shared/api"
import { isSimulateStandalone } from "~/shared/config"

export * from "./async-resource"

/**
 * Инкремент
 * @param value
 */
export function inc(value: number) {
	return value + 1
}

/**
 * Декремент
 * @param value
 */
export function dec(value: number) {
	return value - 1
}

/**
 * Возвращает отрицание переданного булевого значения
 * @param value
 */
export function inv(value: boolean) {
	return !value
}

/**
 * Если value не является массивом, функция вернет массив `[value]`
 * @param value
 * @returns
 */
export function toArray<T>(value: T | T[]) {
	if (value instanceof Array) return value
	return [value]
}

/**
 * Создает задержку на указанное время в миллисекундах. *
 * Эта функция возвращает Promise, который разрешается после указанного времени.
 * Это полезно для имитации задержек в асинхронном коде.
 * @param time время задержки в миллисекундах
 */
export const delay = async (time : number) =>
	new Promise((resolve) => setTimeout(resolve, time))

/**
 * Если переданный аргумент `valueOrFn` - это функция, то она будет вызвана с переданными аргументами `args`,
 * а её результат будет возвращен. Иначе, вернется значение `valueOrFn`.
 * @param valueOrFn возможная функция
 * @param args аргументы функции
 */
export const callIfFunc = (valueOrFn : any, ...args: any[]) =>
	typeof valueOrFn === "function" ? valueOrFn(...args) : valueOrFn

/**
 * Если сайт открыт как PWA-приложение, то вернет `true`
 */
export function isStandalone() {
	return isSimulateStandalone || window.matchMedia("(display-mode: standalone)").matches
}

/**
 * Извлекает сообщение об ошибке. Используется при обработке исключений там, где происходит вызов api.
 * Учитывает axios и fetch (предполагается, что ошибка возвращается в виде _JSON_ с полем `error` или `message`).
 * Внутри используется {@link extractOracleErrorMessage}
 * @param err
 */
export async function getErrorMessage(err: unknown) {
	if (axios.isAxiosError(err)) {
		return extractOracleErrorMessage(err.response?.data.message ?? err.response?.data.error ?? err.message)
	} else if (err instanceof Error) {
		return err.message
	} else if (err instanceof Response) {
		const json = await err.json()
		return extractOracleErrorMessage(json.error || json.message)
	} else if (typeof err === "string") {
		return err
	} else {
		return "Неизвестная ошибка"
	}
}

/**
 * Создает инстанс Error с текстом, извлеченным, с помощью {@link getErrorMessage}, из переданного объекта `err`
 * @param err
 */
export async function createErrorFrom(err: unknown) {
	return new Error(await getErrorMessage(err))
}

/**
 * Выбрасывает исключение, используя инстанс Error, созданый с помощью {@link createErrorFrom}
 * @param err
 */
export async function throwErrorFrom(err: unknown) {
	throw await createErrorFrom(err)
}

export function scrollToTop<T extends HTMLElement, P extends HTMLElement>(el: T, parent: P, margin: number = 0) {
	if (parent && el) {
		parent.scrollTo({
			top: el.offsetTop - parent!.offsetTop - margin,
			behavior: "smooth"
		})
	}
}

/**
 * Используется при фильтрации массива с помощью `.filter`, чтобы убрать falsable значения
 * @param value
 */
export function isDefined<T>(value: T | false | null | undefined): value is T {
	return Boolean(value)
}

export const readFileIntoArrayBuffer = async fd =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onerror = reject;
		reader.onload = () => {
			resolve(reader.result);
		};
		reader.readAsArrayBuffer(fd);
	});

export const saveDataToFile = (data: BlobPart, fileName : string, mimeType : string) => {
	const blob = new Blob([data], { type: mimeType });
	const url = window.URL.createObjectURL(blob);
	downloadURL(url, fileName);
	setTimeout(() => {
		window.URL.revokeObjectURL(url);
	}, 1000);
};

export const downloadURL = (url : string, fileName : string) => {
	const a = document.createElement('a');
	a.href = url;
	a.download = fileName;
	document.body.appendChild(a);
	a.style = 'display: none';
	a.click();
	a.remove();
};